import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Badge } from '@capawesome/capacitor-badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { onNotificationsRead } from '@/lib/unreadCountBus';

const setBadgeCount = async (count: number) => {
  if (Capacitor.isNativePlatform()) {
    // Real native badge via the Capawesome plugin
    if (count > 0) {
      await Badge.set({ count });
    } else {
      await Badge.clear();
    }
    return;
  }
  // Web fallback (supported browsers only)
  if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
    if (count > 0) {
      (navigator as any).setAppBadge(count);
    } else {
      (navigator as any).clearAppBadge();
    }
  }
};

/**
 * Syncs unread notification count to the app badge.
 * Clears instantly when the Notifications page marks all as read.
 */
export const useAppBadge = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const updateBadgeCount = async () => {
      try {
        const { count, error } = await supabase
          .from('notifications')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('is_read', false);

        if (error) {
          console.error('[useAppBadge] Error fetching unread count:', error);
          return;
        }

        await setBadgeCount(count || 0);
      } catch (error) {
        console.error('[useAppBadge] Error updating badge:', error);
      }
    };

    updateBadgeCount();

    // Clear badge when app comes to foreground
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Small delay to allow notification reads to propagate
        setTimeout(updateBadgeCount, 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Instant sync when the Notifications page marks notifications read
    const offBus = onNotificationsRead(({ all }) => {
      if (all) {
        setBadgeCount(0).catch(() => {});
      } else {
        updateBadgeCount();
      }
    });

    // Subscribe to notification changes to keep badge in sync
    const channel = supabase
      .channel('badge-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          updateBadgeCount();
        }
      )
      .subscribe();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      offBus();
      supabase.removeChannel(channel);
    };
  }, [user]);
};
